import React, { ComponentType } from 'react'
import { Pressable, ScrollView, Switch, Text, View } from 'react-native'
import styled from 'styled-components/native'
import { Feather } from '../../../components/icon/vectorIcons'
import useTranslation from '../../../hooks/useTranslation'
import { Block, BlockPropsByType, BlockType } from '../../../doclets/types'
import { supportsFullWidth } from '../../../doclets/registry'
import { builderColors } from './builderColors'
import { BlockSettingsProps, HelpText, SettingLabel } from './settingsStyles'
import ImageBlockSettings from './blocks/ImageBlockSettings'
import ImageTextBlockSettings from './blocks/ImageTextBlockSettings'

// Settings particular to a block type, shown below the common ones
const TYPE_SETTINGS: {
  [T in BlockType]?: ComponentType<BlockSettingsProps<T>>
} = {
  image: ImageBlockSettings,
  imageText: ImageTextBlockSettings
}

interface BlockSettingsPanelProps {
  block: Block
  onToggleHighlight: () => void
  onToggleFullWidth: () => void
  onChange: (props: BlockPropsByType[BlockType]) => void
  onDone: () => void
}

/**
 * The sidebar while a block is selected: that block's settings. Settings every
 * block has (highlight, and full width where the type supports it) come
 * first; block types can add their own below.
 * "All blocks" (like clicking the page background or pressing Esc) deselects,
 * which returns the sidebar to the block list.
 */
const BlockSettingsPanel = ({
  block,
  onToggleHighlight,
  onToggleFullWidth,
  onChange,
  onDone
}: BlockSettingsPanelProps) => {
  const { t } = useTranslation()
  const TypeSettings = TYPE_SETTINGS[block.type] as
    | ComponentType<BlockSettingsProps>
    | undefined

  return (
    <ScrollView contentContainerStyle={{ padding: 16 }}>
      <Pressable
        accessibilityRole="button"
        onPress={onDone}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          alignSelf: 'flex-start',
          marginBottom: 16
        }}
      >
        <Feather name="arrow-left" size={14} color={builderColors.tag} />
        <BackText>{t('doclets:builder:allBlocks')}</BackText>
      </Pressable>
      <Heading>
        {t('doclets:builder:blockSettings', {
          block: t(`doclets:blocks:${block.type}:label`)
        })}
      </Heading>
      <SwitchSetting
        label={t('doclets:builder:highlight')}
        help={t('doclets:builder:highlightHelp')}
        value={!!block.highlighted}
        onToggle={onToggleHighlight}
      />
      {supportsFullWidth(block) && (
        <SwitchSetting
          label={t('doclets:builder:fullWidth')}
          help={t('doclets:builder:fullWidthHelp')}
          value={!!block.fullWidth}
          onToggle={onToggleFullWidth}
        />
      )}
      {TypeSettings && (
        <TypeSettings
          // Fresh state per block (e.g. the image link check)
          key={block.id}
          block={block}
          onChange={onChange}
        />
      )}
    </ScrollView>
  )
}

interface SwitchSettingProps {
  label: string
  help: string
  value: boolean
  onToggle: () => void
}

const SwitchSetting = ({
  label,
  help,
  value,
  onToggle
}: SwitchSettingProps) => (
  <SettingRow>
    <View style={{ flex: 1, marginRight: 12 }}>
      <SettingLabel>{label}</SettingLabel>
      <HelpText>{help}</HelpText>
    </View>
    <Switch
      accessibilityLabel={label}
      value={value}
      onValueChange={onToggle}
      thumbColor="#FFFFFF"
      // @ts-expect-error react-native-web only
      activeThumbColor="#FFFFFF"
      trackColor={{
        true: builderColors.selectedBorder,
        false: builderColors.border
      }}
    />
  </SettingRow>
)

const BackText = styled(Text)`
  margin-left: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${builderColors.tag};
`

const Heading = styled(Text)`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: ${builderColors.text};
  text-transform: uppercase;
  margin-bottom: 12px;
`

const SettingRow = styled(View)`
  flex-direction: row;
  align-items: center;
  padding: 12px 0;
  border-top-width: 1px;
  border-top-color: ${builderColors.border};
`

export default BlockSettingsPanel
